/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { ICorrespondenceConfig } from 'app/core/models/essette-interfaces';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { Correspondence } from 'app/core/components/correspondence/correspondence';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class CaseCorrespondencePage extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    protected caseId!: string;
    protected readonly: boolean = false;
    protected canEditClosedCase: boolean = false;
    protected hasCaseRoute = false;
    protected memberId: string | null = null;

    protected correspondenceConfig: ICorrespondenceConfig = {
        transitionalPageId: '604',
        moduleCode: 'CM',
        serviceRootUrl: 'cm/cases',
        rollupRootUrl: 'cm/cases/rollup/correspondence',
        targetType: 'CM.Case',
        addTargetProperty: this.addCaseProperty.bind(this),
        targetColumnHeader: 'Case #',
        targetColumnProperty: 'case.number',
        targetInclude: ['attachedTo.type', 'case', 'task'],
        targetFields: ['attachedTo.id', 'attachedTo.type.code','case.member.id', 'case.number', 'task.description'],
        previewTransitionalPageId: '3118',
        rollupByMember: true,
        targetSummaryTooltip: 'cm:case.summary.$long'
    };

    private subscriptions: Array<Subscription> = [];


    ////****************** Element References ***************************************////
    protected $correspondence!: Correspondence;


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly context: CasePageContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate(params: { caseId: string }): Promise<boolean> {
        if(!params.caseId) { throw new Error('Missing or invalid caseId parameter'); }

        return true;
    }

    protected async activate(params: { caseId: string }): Promise<void> {
        if(this.caseId !== params.caseId) { this.caseId = params.caseId; }

        this.checkPermissions();

        this.memberId = this.context.case?.member?.id || null;

        this.subscriptions.push(
            this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => this.handleCaseChangeEvent(e))
        );
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected async handleCaseChangeEvent(e: CaseChangeEvent) {
        if(e.case && e.case.id === this.caseId) {
            await this.accountContext.load();
            this.canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
            this.readonly = this.context.caseIsReadOnly();
            this.$correspondence.reload();
        };
    }


    ////****************** Functions *************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
        this.readonly = this.context.caseIsReadOnly();
        if(this.accountContext.hasRouteAccess('cm.case.summary')) {
            this.correspondenceConfig.openTargetSummary = this.openSummary.bind(this);
        }
    }

    public addCaseProperty(letter: CM.CaseCorrespondenceLetter): void {
        letter.case = { id: this.caseId };
    }

    public openSummary(letter: CM.CaseCorrespondenceLetter): void {
        if(letter.case) openCaseSummary(letter.case, this.hasCaseRoute, this.readonly);
    }
}
